import { Link, useNavigate, useLocation } from "react-router-dom";
import { Mail, ExternalLink } from "lucide-react";

export default function Footer({ homePath = "/" }) {
  const currentYear = new Date().getFullYear();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Determine if we're on a B2B/Partners context
  const isPartnersContext = homePath === "/partners" || location.pathname === "/partners" || location.pathname.startsWith("/partners");

  const handleNavClick = (e, path) => {
    e.preventDefault();
    
    // For anchor links like /#pricing
    if (path.startsWith("/#")) {
      const targetId = path.substring(2);
      const targetHomePath = isPartnersContext ? "/partners" : "/";
      
      if (location.pathname === targetHomePath) {
        const element = document.getElementById(targetId);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      } else {
        navigate(targetHomePath, { state: { scrollTo: targetId } });
      }
    } else {
      navigate(path);
      window.scrollTo(0, 0);
    }
  };

  return (
    <footer className="bg-slate-900 text-white py-16" data-testid="footer">
      <div className="container-custom">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="md:col-span-2">
            <h3 className="text-2xl font-bold mb-2">Cliptour</h3>
            <p className="text-slate-400 mb-6">AI powered, human storytelling.</p>
            <a 
              href="mailto:contact@cliptour.ai" 
              className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
            >
              <Mail className="w-4 h-4" />
              contact@cliptour.ai
            </a>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold mb-4">Quick Links</h4>
            <nav className="flex flex-col gap-3">
              <a 
                href={isPartnersContext ? "/partners" : "/"}
                onClick={(e) => { 
                  e.preventDefault(); 
                  navigate(isPartnersContext ? "/partners" : "/"); 
                  window.scrollTo(0, 0); 
                }}
                className="text-slate-400 hover:text-white transition-colors cursor-pointer"
              >
                Home
              </a>
              <a 
                href="/#pricing" 
                onClick={(e) => handleNavClick(e, "/#pricing")}
                className="text-slate-400 hover:text-white transition-colors cursor-pointer"
              >
                Pricing
              </a>
              <a 
                href="/#faq" 
                onClick={(e) => handleNavClick(e, "/#faq")}
                className="text-slate-400 hover:text-white transition-colors cursor-pointer"
              >
                FAQ
              </a>
              <a 
                href="/contact"
                onClick={(e) => handleNavClick(e, "/contact")}
                className="text-slate-400 hover:text-white transition-colors cursor-pointer"
              >
                Contact
              </a>
            </nav>
          </div>

          {/* Legal */}
          <div>
            <h4 className="font-semibold mb-4">Legal</h4>
            <nav className="flex flex-col gap-3">
              <Link to="/privacy-policy" onClick={() => window.scrollTo(0, 0)} className="text-slate-400 hover:text-white transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms-of-service" onClick={() => window.scrollTo(0, 0)} className="text-slate-400 hover:text-white transition-colors">
                Terms of Service
              </Link>
            </nav>
          </div>
        </div>

        {/* Bottom */}
        <div className="mt-12 pt-8 border-t border-slate-800">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <p className="text-slate-500 text-sm">
              © {currentYear} Cliptour. All rights reserved.
            </p>
            <p className="text-slate-500 text-sm">
              We also offer Matterport to Video →{" "}
              <a 
                href="https://3dtovideo.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-slate-400 hover:text-white inline-flex items-center gap-1"
              >
                3dtovideo.com
                <ExternalLink className="w-3 h-3" />
              </a>
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
